import { useState } from 'react';

import clsx from 'clsx';

import Button from 'components/Button';
import { BuyerIntentLeadClick } from 'components/BuyerIntent';
import Card from 'components/Card';
import ClickoutWrapper from 'components/ClickoutWrapper';
import CtaButton from 'components/CtaButton';
import Icon from 'components/Icon';
import Typography from 'components/Typography';
import { useEventContext } from 'context/EventContext';
import usePplForm from 'hooks/usePplForm';

import PricingDisplay from './PricingDisplay';

import classes from './PricingPlanCard.module.scss';

const MAX_ATTRIBUTES_TO_SHOW = 15;

/** @type {import("./types").default} */
const PricingPlanCard = ({
  listing,
  currentPlan,
  className,
  position,
  showPplPricingBanner,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const eventContext = useEventContext({
    name: 'engagement_product_clickout',
    engagementComponent: 'product_pricing-card',
    engagementDestination: 'go-to_vendor-website',
  });

  const plansHaveAttributes =
    listing.pricing.plans.flatMap((plan) => plan.attributes).length > 0;
  const showAttributesEmptyState =
    plansHaveAttributes && !currentPlan.attributes.length;
  const hasFreeTrial = !!listing.cta_urls.trial;
  const showPlaceHolder =
    currentPlan.priceType === 1 && !currentPlan.startingPrice;

  const showPplButton =
    showPplPricingBanner && !listing.is_premium && currentPlan.priceType !== 1;

  const { buyerIntentProductId, buyerIntentCategoryId, handleClick } =
    usePplForm({
      productId: listing.global_product_id,
      sourceLocation: 'listing-detail/pricing-plan-card:get-price',
      formTemplate: 'price',
      includeProductInConfig: true,
      enabled: showPplButton,
    });

  return (
    <Card className={clsx('PricingCard', classes.root, className)}>
      <Typography
        component={'h3'}
        variant="heading-sm"
        className={classes.title}
      >
        {currentPlan.name}
      </Typography>

      <PricingDisplay plan={currentPlan} hidePricingModel={true} />

      {showPlaceHolder && <div className={classes.placeholder} />}
      {listing.is_premium && (
        <ClickoutWrapper
          listingPosition={position}
          isClickout={
            hasFreeTrial ? listing.cta_urls.trial : listing.is_premium
          }
          className={classes.ctaButton}
          url={listing.cta_urls.cta}
          buyerIntentProductId={listing.global_product_id}
          buyerIntentSourceLocation="pricing-plan-card:visit-website"
          buyerIntentTrackAdClick={listing.is_premium}
          {...eventContext.addEvent({
            productClickPosition: position,
            engagementDetails: hasFreeTrial
              ? 'button_try-for-free'
              : 'button_visit-website',
          })}
        >
          <CtaButton variant="outlined" size="large" fullWidth={true}>
            {hasFreeTrial ? 'try for free' : 'visit website'}{' '}
            <Icon icon={['far', 'external-link']} size="16" />
          </CtaButton>
        </ClickoutWrapper>
      )}
      {showPplButton && (
        <BuyerIntentLeadClick
          productId={buyerIntentProductId}
          categoryId={buyerIntentCategoryId}
          sourceLocation="listing-detail/pricing-plan-card:get-price"
        >
          <div className={classes.ctaButton}>
            <CtaButton
              size="large"
              fullWidth={true}
              onClick={handleClick}
              {...eventContext.addEvent({
                engagementComponent: 'ppl_banner',
                engagementDestination: 'go-to_ppl-price-form',
                engagementDetails: 'pricing-plan-card_get-price',
                name: 'ppl_cta',
              })}
            >
              Get Price
            </CtaButton>
          </div>
        </BuyerIntentLeadClick>
      )}
      {plansHaveAttributes && <div className={classes.divider} />}
      {!!currentPlan.attributes.length && (
        <div className={classes.extraFeatures}>
          <Typography variant="heading-sm">Features included:</Typography>
          <ul>
            {currentPlan.attributes.map((attribute, i) => (
              <li
                key={attribute}
                className={clsx({
                  [classes.hidden]:
                    i + 1 > MAX_ATTRIBUTES_TO_SHOW && !isExpanded,
                })}
              >
                {attribute}
              </li>
            ))}
          </ul>
          {currentPlan.attributes.length > MAX_ATTRIBUTES_TO_SHOW && (
            <div className={classes.buttonContainer}>
              <Button
                onClick={() => setIsExpanded(!isExpanded)}
                className={classes.expandButton}
                {...eventContext.addEvent({
                  name: 'engagement_product_click',
                  engagementComponent: 'product_pricing',
                  engagementDestination: isExpanded
                    ? 'collapse_list'
                    : 'expand_list',
                  engagementDetails: isExpanded
                    ? 'text-link_collapse-list'
                    : 'text-link_expand-list',
                })}
              >
                {isExpanded ? 'Collapse list' : 'Expand list'}
                <Icon
                  icon={['far', `chevron-${isExpanded ? 'up' : 'down'}`]}
                  size={16}
                />
              </Button>
            </div>
          )}
        </div>
      )}
      {showAttributesEmptyState && (
        <Typography className={classes.emptystate}>
          Contact vendor for more details
        </Typography>
      )}
    </Card>
  );
};

export default PricingPlanCard;
